import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { CloseOutlined } from '@fx-ui/fine-design';
import classNames from 'classnames';
import { ErrorInput } from '@fx-ui/jdy-design-biz';
import {
    assign,
    EMPTY_FN,
    escapeRegexp,
    forEach,
    get,
    isEmpty,
    isNumber,
    map,
    size as lodashSize,
    trim,
    useDebouncedValue
} from '@libs/shared';
import HighLightText from '../highlight-text';
import { KEY_CODE, platformIntl } from '../../constants';
import { post } from '../../utils';

interface IAsync {
    url: string;
    data: any;
    config?: any;
}

interface ISearchListProps {
    className?: string;
    value: string;
    async?: IAsync;
    items?: any[];
    showSearchResult?: boolean;
    placeholder?: string;
    onPopupVisibleChange?: (visible: boolean) => void;
    onReturn?: (item: any) => void;
    onInputChange?: (val: string) => void; // 防抖后的输入响应
    onItemClick?: (item: any) => void;
    onAsyncSuccess?: (res: any) => void;
    maxLength?: number;
    errMsg?: string;
    maxShow?: number;
    debounceTime?: number; // 延迟搜索触发时间，ms
}

const SearchList = React.forwardRef((props: ISearchListProps, ref) => {
    const {
        className,
        value,
        async,
        items = [],
        maxShow = 30,
        showSearchResult = true,
        placeholder = platformIntl.get('search.placeholder'),
        onPopupVisibleChange = EMPTY_FN,
        onReturn,
        onInputChange,
        onItemClick,
        onAsyncSuccess,
        maxLength,
        errMsg,
        debounceTime = 500
    } = props;

    const [val, setVal] = useState('');
    const [debouncedVal, setDebouncedVal] = useState('');
    const [showPopover, debouncedShowPopover, setShowPopover] = useDebouncedValue(false, 500);
    const [searching, setSearching] = useState<boolean>(false);
    const [currentIdx, setCurrentIdx] = useState<number>(0);

    const searchRef = useRef<any>();
    const inputRef = useRef<any>();
    const listRef = useRef<any>();
    const thisRef = useRef<any>({
        isFocus: false,
        timer: null
    });
    const compositionRef = useRef<boolean>(false);
    const searchTimerRef = useRef<NodeJS.Timeout>();

    const focus = () => {
        inputRef.current.focus();
    };

    const closePopover = () => {
        onPopupVisibleChange(false);
        setShowPopover(false);
    };

    useImperativeHandle(ref, () => {
        return {
            focus,
            closePopover
        };
    });

    useEffect(() => {
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }
    }, []);

    useEffect(() => {
        if (value !== val.trim()) {
            setVal(value);
        }
    }, [value]);

    const handleChange = (v: string) => {
        setVal(v);
        if (!compositionRef.current) {
            const value = v.trim();
            setShowPopover(!isEmpty(value));
            onPopupVisibleChange(!isEmpty(value));
            const searchTimer = searchTimerRef.current;
            if (searchTimer) {
                clearTimeout(searchTimer);
            }
            searchTimerRef.current = setTimeout(() => {
                // 如果搜索前后关键字没有发生变化，则不需要触发重新搜索
                if (v === debouncedVal) {
                    return;
                }
                setDebouncedVal(v);
                setCurrentIdx(0);
                if (listRef.current) {
                    listRef.current.scrollTop = 0;
                }
                const key = v.trim();
                if (key) {
                    if (showSearchResult) {
                        if (async) {
                            setSearching(true);
                            getAsyncItems(key);
                        }
                    }
                }
                onInputChange?.(key);
            }, debounceTime);
        }
    };

    const getAsyncItems = (key: string) => {
        const { url, data } = async!;
        assign(data, { keyword: key });
        post(url, data)
            .then((res) => {
                onAsyncSuccess && onAsyncSuccess(res);
            })
            .finally(() => {
                setSearching(false);
            });
    };

    /**
     * 根据关键词过滤items
     *
     * @param dealItem 是否需要处理item
     */
    const getFilterItems = (dealItem?: boolean) => {
        const filter: any[] = [];
        const key = escapeRegexp(trim(debouncedVal));
        if (!key) {
            return filter;
        }
        forEach(items, (item) => {
            const originItem = assign({}, item);
            const name = get(item, 'name');
            if (name && new RegExp(key, 'i').test(name)) {
                if (dealItem) {
                    const temp = assign({ originItem }, item, {
                        fx_filterName: [name]
                    });
                    const hightLIghtText = (
                        <HighLightText text={name} keyword={key} abbreviated={false} />
                    );
                    assign(temp, { fx_filterName: [hightLIghtText] });
                    filter.push(temp);
                } else {
                    filter.push(assign({ originItem }, item));
                }
                if (filter.length >= maxShow) {
                    return false;
                }
            } else {
                if (dealItem) {
                    const temp = assign({ originItem }, item, {
                        fx_filterName: [name]
                    });
                    filter.push(temp);
                } else {
                    filter.push(assign({ originItem }, item));
                }
                if (filter.length >= maxShow) {
                    return false;
                }
            }
        });
        return filter;
    };

    const handleClose = () => {
        if (thisRef.current.timer) {
            clearTimeout(thisRef.current.timer);
            if (thisRef.current.isFocus) {
                focus();
            }
        }
        handleChange('');
        closePopover();
    };

    const handleKeydown = (event: React.KeyboardEvent) => {
        switch (event.keyCode) {
            case KEY_CODE.ENTER: {
                const currentItem = getCurrentItem();
                if (currentItem) {
                    closePopover();
                    onReturn && onReturn(currentItem.originItem);
                    onInputChange && onInputChange(get(currentItem, 'name'));
                }
                break;
            }
            case KEY_CODE.DOWN:
                dealDownKeydown();
                break;
            case KEY_CODE.UP:
                dealUpKeydown();
                break;
            default:
                break;
        }
    };

    const dealDownKeydown = () => {
        if (listRef.current) {
            const firstItem = listRef.current.querySelector('.filter-item');
            if (firstItem) {
                const itemHeight = firstItem.clientHeight;
                const filterItems = getFilterItems();
                const maxLength = filterItems.length;
                if (currentIdx === maxLength - 1) {
                    setCurrentIdx(0);
                    listRef.current.scrollTop = 0;
                } else {
                    setCurrentIdx((idx) => idx + 1);
                    if (currentIdx + 1 === maxLength - 1) {
                        listRef.current.scrollTop = (maxLength - 1) * itemHeight;
                    } else {
                        const minScrollTop = (currentIdx + 1 - 7) * itemHeight;
                        if (minScrollTop > listRef.current.scrollTop) {
                            listRef.current.scrollTop = minScrollTop;
                        }
                    }
                }
            }
        }
    };

    const dealUpKeydown = () => {
        if (listRef.current) {
            const firstItem = listRef.current.querySelector('.filter-item');
            if (firstItem) {
                const itemHeight = firstItem.clientHeight;
                if (currentIdx === 0) {
                    const filterItems = getFilterItems();
                    const maxLength = filterItems.length;
                    setCurrentIdx(maxLength - 1);
                    listRef.current.scrollTop = (maxLength - 1) * itemHeight;
                } else {
                    setCurrentIdx((idx) => idx - 1);
                    const maxScrollTop = (currentIdx - 1) * itemHeight;
                    if (maxScrollTop < listRef.current.scrollTop) {
                        listRef.current.scrollTop = maxScrollTop;
                    }
                }
            }
        }
    };

    const getCurrentItem = () => {
        const filterItems = getFilterItems();
        if (isEmpty(filterItems)) {
            return null;
        }
        return filterItems![currentIdx];
    };

    const renderContent = () => {
        const filterItems = getFilterItems(true);
        return (
            <ul className="item-list" ref={listRef}>
                {searching || debouncedVal !== val ? (
                    <li className="empty-content">{platformIntl.get('searching')}</li>
                ) : (
                    <>
                        {map(filterItems, (item, index) => {
                            return (
                                <li
                                    className={classNames('filter-item', {
                                        select: index === currentIdx
                                    })}
                                    title={get(item, 'name')}
                                    onClick={() => {
                                        closePopover();
                                        onInputChange && onInputChange(get(item, 'name'));
                                        onItemClick && onItemClick(item.originItem);
                                    }}
                                    onMouseEnter={() => setCurrentIdx(index)}
                                    key={index}
                                >
                                    <div className="filter-name">{item.fx_filterName}</div>
                                </li>
                            );
                        })}
                        {filterItems.length >= maxShow && (
                            <li className="empty-content">
                                {platformIntl.get('search.max.show.tip', { count: maxShow })}
                            </li>
                        )}
                        {isEmpty(filterItems) && (
                            <li className="empty-content">
                                {platformIntl.get('search.not.found')}
                            </li>
                        )}
                    </>
                )}
            </ul>
        );
    };

    return (
        <div className={classNames('fx-search-list', 'size-normal', className)} ref={searchRef}>
            <ErrorInput
                style={{ width: '100%' }}
                errMsg={errMsg}
                inputState={
                    isNumber(maxLength) && lodashSize(trim(val)) > maxLength ? 'error' : undefined
                }
                ref={inputRef}
                value={val}
                onCompositionStart={() => {
                    compositionRef.current = true;
                }}
                onCompositionEnd={(c) => {
                    const target = c.target as HTMLInputElement;
                    compositionRef.current = false;
                    handleChange(target.value);
                }}
                onChange={handleChange}
                className="normal-style-input"
                suffix={val.trim() ? <CloseOutlined onClick={handleClose} /> : undefined}
                placeholder={placeholder}
                onBlur={() => {
                    thisRef.current.timer = setTimeout(() => {
                        thisRef.current.isFocus = false;
                    }, 200);
                }}
                onFocus={() => {
                    thisRef.current.isFocus = true;
                }}
                onKeyDown={handleKeydown}
                errorTrigger="blur"
            />
            {showSearchResult && showPopover && debouncedShowPopover && renderContent()}
        </div>
    );
});

export default SearchList;
